import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Ban, Clock, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  useToast,
  type SelectOption,
} from '../../ui';
import { useBlockers } from '../../features/blockers/useBlockers';
import { filterBlockers } from '../../features/blockers/blockerModel';
import type {
  BlockerFilter,
  BlockerSummary,
} from '../../features/blockers/types';
import { BlockerBoard } from './BlockerBoard';
import { BlockerModal } from './BlockerModal';

const EMPTY_FILTER: BlockerFilter = { status: 'OPEN', scope: '' };

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
];
const SCOPE_OPTIONS: SelectOption[] = [
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'EXTERNAL', label: 'External' },
];

/**
 * Blockers board — the structured impediments the team is tracking. Each card
 * shows how long it has been blocking ("BLOCKED — N DAYS"), the affected goal
 * and forecast delivery impact, and the dependency owner. Defaults to the open
 * blockers; the forecast impact is also surfaced as a header stat so it can
 * feed goal-health / forecast awareness.
 */
export function BlockersPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    blockers,
    goals,
    workstreams,
    tasks,
    currentAlias,
    goalTitles,
    workstreamTitles,
    storyTitles,
    overallImpact,
    loading,
    error,
    create,
    resolve,
    reopen,
    remove,
  } = useBlockers();

  const [filter, setFilter] = useState<BlockerFilter>(EMPTY_FILTER);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<BlockerSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const filtered = useMemo(
    () => filterBlockers(blockers, filter),
    [blockers, filter],
  );
  const openCount = useMemo(
    () => blockers.filter((b) => b.status === 'OPEN').length,
    [blockers],
  );
  const filtersActive = !!(filter.status || filter.scope);

  const onCreate = async (draft: Parameters<typeof create>[0]) => {
    const saved = await create(draft);
    toast({ tone: 'success', title: 'Blocker saved', description: saved.summary });
    return saved;
  };

  const onResolve = async (b: BlockerSummary) => {
    await resolve(b.id);
    toast({ tone: 'success', title: 'Blocker resolved', description: b.summary });
  };
  const onReopen = async (b: BlockerSummary) => {
    await reopen(b.id);
    toast({ tone: 'info', title: 'Blocker reopened', description: b.summary });
  };
  const onDelete = (b: BlockerSummary) => setPendingDelete(b);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      toast({ tone: 'success', title: 'Blocker removed', description: pendingDelete.summary });
      setPendingDelete(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove blocker',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={<Ban className="h-5 w-5" />}
        title="Blockers"
        description="Structured impediments — who's blocked, by whom, for how long, and the delivery impact."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
            New blocker
          </Button>
        }
      />

      {/* Impact summary. */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Badge tone={openCount > 0 ? 'danger' : 'success'} dot>
          {openCount} open {openCount === 1 ? 'blocker' : 'blockers'}
        </Badge>
        {overallImpact.totalForecastImpactDays > 0 && (
          <Badge tone="warning">
            <Clock className="h-3 w-3" /> ~{overallImpact.totalForecastImpactDays} forecast
            impact {overallImpact.totalForecastImpactDays === 1 ? 'day' : 'days'}
          </Badge>
        )}
        {overallImpact.hasExternalDependency && (
          <Badge tone="warning">external dependency</Badge>
        )}
      </div>

      {/* Filters. */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select
            label="Status"
            placeholder="All"
            value={filter.status}
            options={STATUS_OPTIONS}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          />
        </div>
        <div className="w-40">
          <Select
            label="Scope"
            placeholder="All"
            value={filter.scope}
            options={SCOPE_OPTIONS}
            onChange={(e) => setFilter((f) => ({ ...f, scope: e.target.value }))}
          />
        </div>
        {filtersActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilter({ status: '', scope: '' })}
          >
            Clear filters
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {filtered.length} of {blockers.length}{' '}
          {blockers.length === 1 ? 'blocker' : 'blockers'}
        </span>
      </div>

      {/* Content. */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading blockers" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load blockers"
          description={error}
        />
      ) : blockers.length === 0 ? (
        <EmptyState
          icon={<Ban className="h-6 w-6" />}
          title="No blockers tracked"
          description="Nothing is blocked — or nothing has been captured yet. Raise a blocker (or convert one from a daily update) so dependencies stay visible."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
              New blocker
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Ban className="h-6 w-6" />}
          title="No blockers match these filters"
          description="Try clearing the filters to see every tracked blocker."
        />
      ) : (
        <BlockerBoard
          blockers={filtered}
          goalTitles={goalTitles}
          workstreamTitles={workstreamTitles}
          storyTitles={storyTitles}
          onOpenGoal={(id) => navigate(`/goals/${encodeURIComponent(id)}`)}
          onOpenWorkstream={(id) => navigate(`/workstreams/${encodeURIComponent(id)}`)}
          onResolve={onResolve}
          onReopen={onReopen}
          onDelete={onDelete}
        />
      )}

      <BlockerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultEngineer={currentAlias}
        goals={goals}
        workstreams={workstreams}
        tasks={tasks}
        onCreate={onCreate}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove blocker?"
        confirmLabel="Remove blocker"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
        message={
          <>
            Remove{' '}
            <span className="font-medium text-zinc-100">{pendingDelete?.summary}</span>?
            This can't be undone.
          </>
        }
      />
    </div>
  );
}

export default BlockersPage;
